
import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import StorageSetupStep from "@/components/Destinations/StorageSetupStep";

interface CreateProjectDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCreateProject: (name: string) => Promise<void>;
}

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  isOpen,
  onOpenChange,
  onCreateProject,
}) => {
  const [newProjectName, setNewProjectName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storageStep, setStorageStep] = useState<{ id: string; name: string } | null>(null);

  const findCreatedProject = async (name: string) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return null;
    const { data } = await supabase
      .from("projects")
      .select("id, name")
      .eq("user_id", auth.user.id)
      .eq("name", name)
      .order("created_at", { ascending: false })
      .limit(1);
    return data?.[0] ?? null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newProjectName.trim()) {
      setError("Projektnamn krävs");
      return;
    }
    
    setError(null);
    setIsLoading(true);
    
    try {
      const name = newProjectName.trim();
      await onCreateProject(name);
      toast.success("Projektet har skapats framgångsrikt");

      const created = await findCreatedProject(name);
      setNewProjectName("");
      if (created) {
        // Steg 2: låt användaren välja var dokumenten ska lagras.
        setStorageStep({ id: created.id, name: created.name });
      } else {
        onOpenChange(false);
      }
    } catch (error: any) {
      console.error("Error creating project:", error);
      setError(error.message || "Kunde inte skapa projektet");
      toast.error("Kunde inte skapa projektet");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNewProjectName("");
    setError(null);
    setStorageStep(null);
    onOpenChange(false);
  };

  const finishStorage = () => {
    setStorageStep(null);
    onOpenChange(false);
  };

  if (storageStep) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => (open ? onOpenChange(true) : finishStorage())}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Lagringsplats för {storageStep.name}</DialogTitle>
          </DialogHeader>
          <StorageSetupStep
            projectId={storageStep.id}
            projectName={storageStep.name}
            onDone={finishStorage}
            doneLabel="Till projektet"
          />
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skapa nytt projekt</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="project-name">Projektnamn</Label>
            <Input
              id="project-name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Ange projektnamn"
              disabled={isLoading}
              autoFocus
            />
            {error && (
              <p className="text-sm text-destructive mt-1">{error}</p>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancel}
              disabled={isLoading}
            >
              Avbryt
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || !newProjectName.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Skapar...
                </>
              ) : (
                "Skapa projekt"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;
